import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";

import type { ProfileLine, ProfileRow, ProfileThumbnail } from "../aboutContent";

/**
 * 会社概要の PROFILE 表。項目名 / 内容の 2 カラム定義リスト。
 * 狭い画面では項目名を上に積む。
 *
 * リンクはサイト共通のレギュレーション（下線なし・色継承、hover で青）に揃える。
 * 旧ページにあったサムネイルは、写真（所在地・代表者）はテキストの下に添え、
 * ロゴ（関連施設・顧問）は text を空にしてロゴだけを出す。
 */
const ProfileList = styled.dl`
  margin: 0;
  width: 100%;
`;

/* 1 行 = 項目名 + 内容。区切り線は行に持たせ、項目名と内容の間で途切れないようにする */
const ProfileRowBox = styled.div`
  display: grid;
  grid-template-columns: 136px 1fr;
  column-gap: 24px;
  padding: 16px 0;
  border-top: 1px solid var(--soremo-border);

  &:last-child {
    border-bottom: 1px solid var(--soremo-border);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    row-gap: 4px;
  }
`;

const ProfileTerm = styled.dt.attrs({
  className: "bodytext-13 u-text-light-gray",
})`
  /* 内容 1 行目（bodytext 16px / 200%）とベースラインを揃える */
  padding-top: 0.25em;

  @media (max-width: 767px) {
    padding-top: 0;
  }
`;

const ProfileDescription = styled.dd.attrs({
  className: "bodytext u-col u-gap8",
})`
  margin: 0;
  color: #000;
`;

const LineSub = styled.span.attrs({
  className: "bodytext-11 u-text-light-gray",
})`
  display: block;
`;

/* 注記行（「（順不同・敬称略）」など）。サイズは他と同じで色だけ落とす */
const LineMuted = styled.span.attrs({
  className: "bodytext u-text-light-gray",
})`
  display: block;
`;

const LineLink = styled.a`
  color: inherit;

  &:hover {
    color: var(--soremo-blue);
  }
`;

const Line = styled.span`
  display: block;
`;

const Thumb = styled.img<{ $standalone: boolean }>`
  display: block;
  height: auto;
  /* テキスト行の下に添える場合だけ少し離す */
  margin-top: ${({ $standalone }) => ($standalone ? "0" : "4px")};
`;

const ThumbLink = styled.a`
  display: block;
  width: fit-content;
  line-height: 0;

  &:hover {
    opacity: 0.7;
  }
`;

const isExternal = (href: string) => /^https?:\/\//.test(href);

const withLink = (
  href: string | undefined,
  key: React.Key,
  children: React.ReactNode,
  Wrapper: typeof LineLink | typeof ThumbLink,
) => {
  if (!href) return <React.Fragment key={key}>{children}</React.Fragment>;
  if (isExternal(href)) {
    return (
      <Wrapper key={key} href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </Wrapper>
    );
  }
  return (
    <Wrapper as={Link} key={key} to={href}>
      {children}
    </Wrapper>
  );
};

const renderThumbnail = (thumb: ProfileThumbnail, standalone: boolean) => {
  const img = (
    <Thumb
      src={thumb.src}
      alt={thumb.alt}
      width={thumb.width}
      loading="lazy"
      $standalone={standalone}
    />
  );
  return withLink(thumb.href, "thumb", img, ThumbLink);
};

const renderLine = (line: ProfileLine, index: number) => {
  if (line.muted) {
    return <LineMuted key={index}>{line.text}</LineMuted>;
  }

  const body = (
    <>
      {line.text}
      {line.sub ? <LineSub>{line.sub}</LineSub> : null}
    </>
  );

  const hasText = line.text !== "";
  return (
    <Line key={index}>
      {hasText ? withLink(line.href, "text", body, LineLink) : null}
      {line.thumbnail ? renderThumbnail(line.thumbnail, !hasText) : null}
    </Line>
  );
};

export interface AboutProfileProps {
  rows: ProfileRow[];
}

const AboutProfile: React.FC<AboutProfileProps> = ({ rows }) => {
  return (
    <ProfileList>
      {rows.map((row) => (
        <ProfileRowBox key={row.label}>
          <ProfileTerm>{row.label}</ProfileTerm>
          <ProfileDescription>{row.lines.map(renderLine)}</ProfileDescription>
        </ProfileRowBox>
      ))}
    </ProfileList>
  );
};

export default AboutProfile;
